import { useEffect, useRef, type PointerEvent, type RefObject } from 'react';

import { useBoardViewportStore, useWorkspaceUiStore } from '@walls/store';

export function useBoardPan(
  viewportRef: RefObject<HTMLElement | null>,
  onPanScroll?: (viewport: HTMLElement) => void
) {
  const selectedTool = useWorkspaceUiStore((state) => state.selectedTool);
  const zoom = useWorkspaceUiStore((state) => state.zoom);
  const setCurrentMousePosition = useBoardViewportStore((state) => state.setCurrentMousePosition);
  const startPan = useBoardViewportStore((state) => state.startPan);
  const stopPan = useBoardViewportStore((state) => state.stopPan);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);
  const mousePositionAnimationFrameRef = useRef<number | null>(null);
  const pendingMousePositionRef = useRef<{ x: number; y: number } | null>(null);

  const scheduleMousePosition = (position: { x: number; y: number }) => {
    pendingMousePositionRef.current = position;

    if (mousePositionAnimationFrameRef.current != null) {
      return;
    }

    mousePositionAnimationFrameRef.current = requestAnimationFrame(() => {
      mousePositionAnimationFrameRef.current = null;
      const pendingPosition = pendingMousePositionRef.current;
      pendingMousePositionRef.current = null;

      if (pendingPosition) {
        setCurrentMousePosition(pendingPosition);
      }
    });
  };

  useEffect(
    () => () => {
      if (mousePositionAnimationFrameRef.current != null) {
        cancelAnimationFrame(mousePositionAnimationFrameRef.current);
      }
    },
    []
  );

  return {
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      const viewport = viewportRef.current;

      if (!viewport) {
        return;
      }

      const boardPoint = getBoardPoint(event, viewport, zoom);
      setCurrentMousePosition(boardPoint);

      if (selectedTool === 'hand') {
        event.currentTarget.setPointerCapture(event.pointerId);
        lastPoint.current = { x: event.clientX, y: event.clientY };
        startPan(boardPoint);
      }
    },
    onPointerMove: (event: PointerEvent<HTMLElement>) => {
      const viewport = viewportRef.current;

      if (!viewport) {
        return;
      }

      scheduleMousePosition(getBoardPoint(event, viewport, zoom));

      if (selectedTool !== 'hand' || !lastPoint.current) {
        return;
      }

      viewport.scrollLeft += lastPoint.current.x - event.clientX;
      viewport.scrollTop += lastPoint.current.y - event.clientY;
      lastPoint.current = { x: event.clientX, y: event.clientY };
      onPanScroll?.(viewport);
    },
    onPointerUp: (event: PointerEvent<HTMLElement>) => {
      if (lastPoint.current) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      lastPoint.current = null;
      stopPan();
    },
    onPointerLeave: () => {
      lastPoint.current = null;
      stopPan();
    },
  };
}

function getBoardPoint(
  event: PointerEvent<HTMLElement>,
  viewport: HTMLElement,
  zoom: number
) {
  const rect = viewport.getBoundingClientRect();

  return {
    x: viewport.scrollLeft + (event.clientX - rect.left) / zoom,
    y: viewport.scrollTop + (event.clientY - rect.top) / zoom,
  };
}
